import React, { useEffect, useMemo, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined"
import ShareOutlined from "@mui/icons-material/ShareOutlined"
import { useParams } from "react-router-dom"
import { countries as allCountries, countries } from "../../global/countriesAndSources"
import { getIgo, getLangFromCountry, getTranslation } from "../../HTTP/translation"
import { fetchLinksThematics, getSearchInCseThematic, isNodeId } from "../../HTTP/thematics"
import {
  deleteChatSession,
  getChatHistory,
  getChatSessions,
  renameChatSession,
  thematicChat,
  thematicChatExpertWithThematic,
  thematicChatExpertWithoutThematic,
} from "../../HTTP/ai"
import { ProfileHeader } from "./profileHeader"
import { CountryBar } from "./countryBar"
import Map from "../Map/Map"
import "./GoogleCSE.css"
import "./articles.css"
import "./zemusIa.css"

const RESTRICTED_SEARCH_REGEX = /(adult|anal|aphro|arous|bdsm|cam(girl|sex)|dating|deepth|dildo|domin|escort|erot|explicit|fetish|fuck|gangbang|hardcore|hentai|hookup|incest|kinky|lingerie|masturb|milf|nude|nsfw|orgasm|onlyfan|porn|sex|sext|strip|sugarbaby|swinger|threesome|vagin|vibrator|webcam|xxx)/i
const DEFAULT_IGO_LABEL = "Sélectionner un Forum global"

export default function ZemusIa() {
  sessionStorage.setItem(
    "currentlanguage",
    document.documentElement.lang === "auto" ? navigator.languages[1] : document.documentElement.lang
  )

  const [displayMap, setDisplayMap] = useState(false)
  const [selectedIgo, setSelectedIgo] = useState({ name: DEFAULT_IGO_LABEL, country_list: "" })
  const [igo, setIgo] = useState([])
  const [igoReady, setIgoReady] = useState(false)
  const [links, setLinks] = useState([])
  const [groupsLinks, setGroupsLinks] = useState([])
  const [selectedGroupLinks, setSelectedGroupLinks] = useState(0)
  const [lengthGroupsLinks] = useState(10)
  const [selectedSite, setSelectedSite] = useState(null)
  const [showSiteDropdown, setShowSiteDropdown] = useState(false)
  const [searchQuery, setSearchQuery] = useState(sessionStorage.getItem("search_query") || "")
  const [searchInCse, setSearchInCse] = useState(false)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [advancedSearch, setAdvancedSearch] = useState(false)
  const [sessions, setSessions] = useState([])
  const [sessionId, setSessionId] = useState(null)
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [sessionError, setSessionError] = useState("")
  const [shareMenuId, setShareMenuId] = useState(null)
  const [copyStatus, setCopyStatus] = useState({})
  const messagesEndRef = useRef(null)
  const chatShellRef = useRef(null)
  const url = useMemo(() => new URL(window.location.href), [])
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), [])
  const { countrycode } = useParams()

  const sortSessions = (items) => [...items].sort((first, second) =>
    new Date(second.updated_at).getTime() - new Date(first.updated_at).getTime()
  )

  const loadSessions = async () => {
    setSessionsLoading(true)
    setSessionError("")
    try {
      const response = await getChatSessions()
      setSessions(sortSessions(Array.isArray(response?.sessions) ? response.sessions : []))
    } catch (error) {
      setSessionError("Impossible de charger les conversations.")
    } finally {
      setSessionsLoading(false)
    }
  }

  const selectSession = async (nextSessionId) => {
    setSessionId(nextSessionId)
    setHistoryLoading(true)
    setSessionError("")
    try {
      const response = await getChatHistory(nextSessionId)
      setMessages((response?.chats || []).map((chat) => ({
        id: chat.id,
        role: chat.role === "user" ? "user" : "assistant",
        text: chat.content,
      })))
    } catch (error) {
      setMessages([])
      setSessionError("Impossible de charger cette conversation.")
    } finally {
      setHistoryLoading(false)
    }
  }

  const startNewConversation = () => {
    setSessionId(null)
    setMessages([])
    setSessionError("")
  }

  const renameSession = async (session) => {
    const name = window.prompt("Nom de la conversation", session.name || "")?.trim()
    if (!name || name === session.name) return
    try {
      await renameChatSession(session.id, name)
      setSessions((previous) => previous.map((item) => item.id === session.id ? { ...item, name } : item))
    } catch (error) {
      setSessionError("Impossible de renommer cette conversation.")
    }
  }

  const removeSession = async (session) => {
    if (!window.confirm(`Supprimer « ${session.name || "cette conversation"} » ?`)) return
    try {
      await deleteChatSession(session.id)
      const remaining = sessions.filter((item) => item.id !== session.id)
      setSessions(remaining)
      if (sessionId === session.id) {
        if (remaining[0]) await selectSession(remaining[0].id)
        else startNewConversation()
      }
    } catch (error) {
      setSessionError("Impossible de supprimer cette conversation.")
    }
  }

  useEffect(() => {
    loadSessions()
  }, [])

  const isRestrictedSearchEnabled = (() => {
    try {
      const localUser = JSON.parse(localStorage.getItem("user"))
      return Number(localUser?.restricted_searches) === 1
    } catch (error) {
      return false
    }
  })()

  let country = countrycode ? allCountries.find((c) => c.code === countrycode) : null
  country =
    country ||
    JSON.parse(sessionStorage.getItem("search_country")) ||
    allCountries.find((c) => c.code === JSON.parse(localStorage.getItem("user"))?.country) ||
    { name: "France", code: "fr", cse: "004899679196972058950:gvpguoasgrm" }

  if (JSON.parse(sessionStorage.getItem("search_country")) === null) {
    sessionStorage.setItem("search_country", JSON.stringify(country))
  }

  const [selectedCountry, setSelectedCountry] = useState(country)

  const currentThematicId = sessionStorage.getItem("thematic")
  // Le nom du nœud est mémorisé lors de la sélection ; l'id sert de repli
  const currentThematic = currentThematicId
    ? (isNodeId(currentThematicId) ? (sessionStorage.getItem("thematic_name") || currentThematicId) : currentThematicId)
    : null
  let thematicPath = []
  try {
    thematicPath = JSON.parse(sessionStorage.getItem("thematic_path")) || []
  } catch {
    thematicPath = []
  }
  const thematicLabel = currentThematic && thematicPath.length > 0 && thematicPath[thematicPath.length - 1] === currentThematic
    ? thematicPath.join(" > ")
    : currentThematic
  const selectedTheme = thematicLabel ? `Zemus IA | ${thematicLabel}` : "Zemus IA"

  const getAssistantText = (responseData) => {
    if (typeof responseData === "string") return responseData
    if (!responseData || typeof responseData !== "object") return "Aucune reponse exploitable."
    return (
      responseData.answer ||
      responseData.result ||
      responseData.response ||
      responseData.message ||
      (typeof responseData.data === "string" ? responseData.data : "") ||
      JSON.stringify(responseData)
    )
  }

  const getSuggestedPrompts = (responseData) => [
    responseData?.suggestedPrompt1,
    responseData?.suggestedPrompt2,
  ].filter((prompt, index, prompts) =>
    typeof prompt === "string" && prompt.trim() && prompts.indexOf(prompt) === index
  )

  const copyAssistantMessage = async (messageId, text, successMessage = "Copie !") => {
    try {
      await navigator.clipboard.writeText(text)
      setCopyStatus((previous) => ({ ...previous, [messageId]: successMessage }))
    } catch (error) {
      console.error("Impossible de copier la reponse de Zemus IA:", error)
      setCopyStatus((previous) => ({ ...previous, [messageId]: "Copie impossible" }))
    }
  }

  const getShareLinks = (text) => {
    const pageUrl = window.location.href
    const encodedUrl = encodeURIComponent(pageUrl)
    const encodedText = encodeURIComponent(text)
    const shareTitle = encodeURIComponent("Réponse de Zemus IA")
    return [
      {
        label: "Facebook",
        href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        copyText: "Texte copié : collez-le dans votre publication Facebook.",
      },
      {
        label: "X",
        href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      },
      {
        label: "LinkedIn",
        href: `https://www.linkedin.com/feed/?shareActive=true&text=${encodedText}`,
      },
      {
        label: "E-mail",
        href: `mailto:?subject=${shareTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
      },
    ]
  }

  const getThematicChatErrorMessage = (error) => {
    switch (error?.status) {
      case 400:
        return error.message || "La requete envoyee est invalide."
      case 401:
        return "Votre session a expire. Veuillez vous reconnecter."
      case 403:
        return "Acces refuse pour cette fonctionnalite."
      case 404:
        return error.message || "La session demandee n'existe pas."
      case 429:
        return "Le service est temporairement limite. Veuillez reessayer dans un instant."
      default:
        return "Le service IA est temporairement indisponible. Merci de reessayer plus tard."
    }
  }

  const shortenUrlLabel = (rawUrl) => {
    try {
      const parsed = new URL(rawUrl)
      const host = parsed.hostname.replace(/^www\./, "")
      if (host.includes("vertexaisearch.cloud.google.com")) {
        return "Source Google Vertex"
      }
      return host
    } catch (error) {
      return "Source"
    }
  }

  const normalizeLinksForMarkdown = (text) => {
    if (!text) return ""

    const protectedLinks = []
    const markdownLinksProtected = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (match) => {
      const token = `@@ZEMUS_LINK_${protectedLinks.length}@@`
      protectedLinks.push(match)
      return token
    })

    const withShortLinks = markdownLinksProtected.replace(/https?:\/\/[^\s]+/g, (rawUrl) => {
      let urlToUse = rawUrl
      let trailing = ""
      while (/[.,;!?)]$/.test(urlToUse)) {
        trailing = urlToUse.slice(-1) + trailing
        urlToUse = urlToUse.slice(0, -1)
      }
      return `[${shortenUrlLabel(urlToUse)}](${urlToUse})${trailing}`
    })

    return withShortLinks.replace(/@@ZEMUS_LINK_(\d+)@@/g, (_, index) => protectedLinks[Number(index)] || "")
  }

  const getLinkCountryCode = (link) =>
    link?.country || link?.country_code || link?.countryCode || selectedCountry?.code || "fr"

  const normalizeContextualLinks = (linksToNormalize) =>
    (linksToNormalize || []).filter(Boolean).map((link) => {
      const linkCountryCode = getLinkCountryCode(link)
      return {
        ...link,
        country: linkCountryCode,
      }
    })

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }, 50)
  }

  const scrollToTop = () => {
    if (chatShellRef.current) {
      chatShellRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }
  }

  const handleSiteSelect = (site) => {
    setSelectedSite(site)
    if (site === null) {
      url.searchParams.delete("link-id")
      window.history.pushState({}, "", url)
      sessionStorage.removeItem("selected_site")
    } else {
      url.searchParams.set("link-id", site.id)
      window.history.pushState({}, "", url)
      sessionStorage.setItem("selected_site", JSON.stringify(site))
    }
    setShowSiteDropdown(false)
  }

  const advancedThematicId = isNodeId(currentThematicId) ? Number(currentThematicId) : null
  const advancedCountryCodes = selectedCountry?.code && selectedCountry.code !== "wd" ? [selectedCountry.code] : []
  // Sans nœud sélectionné, l'API choisit elle-même les catégories pertinentes
  const canAdvancedSearch = (query) =>
    Boolean(String(query ?? "").trim()) && advancedCountryCodes.length > 0
  const advancedMissing = advancedCountryCodes.length === 0 ? "Sélectionnez un pays." : ""

  // Les champs optionnels (période, horizon, public, usage, longueur) sont omis tant que l'utilisateur ne les renseigne pas
  const buildAdvancedPayload = (query) => ({
    language: sessionStorage.getItem("currentlanguage") || document.documentElement.lang || "fr",
    query,
    ...(advancedThematicId !== null ? { thematicId: advancedThematicId } : {}),
    countryCodes: advancedCountryCodes,
  })

  const buildContextualPrompt = (query) => {
    // Récupération de la langue
    const language = sessionStorage.getItem("currentlanguage") || document.documentElement.lang || "fr"
    
    // Récupération du code pays
    const countryCode = selectedCountry?.code || "fr"
    
    // Récupération de la thématique
    const thematic = currentThematic || null

    // Récupération des liens thématiques
    let contextualLinks = []
    if (selectedSite) {
      // Un seul lien est sélectionné
      contextualLinks = [selectedSite]
    } else if (!searchInCse) {
      const linksToUse = groupsLinks[Number(selectedGroupLinks)] || []
      contextualLinks = linksToUse
    }
    
    const contextData = {
      language,
      countryCode,
      thematic,
      links: normalizeContextualLinks(contextualLinks),
      query
    }

    return contextData
  }

  const sendMessage = async (providedQuery, advanced = advancedSearch) => {
    const trimmedInput = (providedQuery ?? searchQuery).trim()
    if (!trimmedInput || loading) return

    if (!user?.id) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Utilisateur introuvable. Reconnectez-vous puis reessayez." }
      ])
      return
    }

    if (advanced && !canAdvancedSearch(trimmedInput)) return

    setLoading(true)
    setMessages((prev) => [...prev, { id: `pending-user-${Date.now()}`, role: "user", text: trimmedInput }])
    setSearchQuery("")
    scrollToBottom()

    try {
      const callAdvanced = advancedThematicId !== null
        ? thematicChatExpertWithThematic
        : thematicChatExpertWithoutThematic
      const response = advanced
        ? await callAdvanced(sessionId, buildAdvancedPayload(trimmedInput))
        : await thematicChat(sessionId, buildContextualPrompt(trimmedInput))
      if (response?.sessionId != null && response.sessionId !== sessionId) {
        setSessionId(response.sessionId)
        await loadSessions()
      }
      const assistantText = getAssistantText(response)
      setMessages((prev) => [...prev, {
        id: `pending-assistant-${Date.now()}`,
        role: "assistant",
        text: assistantText,
        suggestions: getSuggestedPrompts(response),
        advanced,
        scope: advanced ? {
          thematic: response?.thematic,
          selectedThematics: Array.isArray(response?.selectedThematics) ? response.selectedThematics : [],
          countries: Array.isArray(response?.countries) ? response.countries : [],
          sources: Array.isArray(response?.sources) ? response.sources : [],
        } : null,
      }])
    } catch (error) {
      if (advanced && error?.status === 404) setSessionId(null)
      const errorMessage = getThematicChatErrorMessage(error)
      setSessionError(errorMessage)
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: errorMessage, retry: { query: trimmedInput, advanced } }
      ])
    } finally {
      setLoading(false)
      scrollToBottom()
    }
  }

  const handleSearch = async () => {
    let finalQuery = searchQuery

    if (isRestrictedSearchEnabled && RESTRICTED_SEARCH_REGEX.test(finalQuery)) {
      alert("Cette recherche est restreinte pour votre compte.")
      setSearchQuery("")
      sessionStorage.removeItem("search_query")
      return
    }

    if (document.getElementById("translate_query") && document.getElementById("translate_query").checked) {
      try {
        let queryToTranslate = searchQuery
        let beforePipe = ""
        if (searchQuery.includes("|")) {
          queryToTranslate = searchQuery.split("|")[1].trim()
          beforePipe = searchQuery.split("|")[0].trim()
        }
        const langDest = await getLangFromCountry(selectedCountry.code)
        const translated = await getTranslation(
          sessionStorage.getItem("currentlanguage"),
          langDest,
          queryToTranslate
        )
        finalQuery = beforePipe.length > 0 ? `${beforePipe} | ${translated}` : translated
        setSearchQuery(finalQuery)
      } catch (error) {
        console.error("Erreur lors de la traduction:", error)
      }
    }

    if (isRestrictedSearchEnabled && RESTRICTED_SEARCH_REGEX.test(finalQuery)) {
      alert("Cette recherche est restreinte pour votre compte.")
      setSearchQuery("")
      sessionStorage.removeItem("search_query")
      return
    }

    sessionStorage.setItem("search_query", finalQuery)

    if (selectedSite) {
      sessionStorage.setItem("selected_site", JSON.stringify(selectedSite))
    } else {
      sessionStorage.removeItem("selected_site")
    }

    await sendMessage(finalQuery)
  }

  useEffect(() => {
    const funIgo = async () => {
      const res = await getIgo()
      setIgo(res)
      const currentUrl = new URL(window.location.href)
      if (currentUrl.searchParams.get("igo") !== null) {
        const igoSelected = res.find((item) => item.name === currentUrl.searchParams.get("igo"))
        if (igoSelected) {
          setSelectedIgo(igoSelected)
        } else if (res[0]) {
          currentUrl.searchParams.delete("igo")
          setSelectedIgo(res[0])
        }
      } else if (sessionStorage.getItem("search_igo") !== null) {
        const igoFound = res.find((item) => item.name === sessionStorage.getItem("search_igo"))
        if (igoFound) {
          setSelectedIgo(igoFound)
        } else if (res[0]) {
          sessionStorage.removeItem("search_igo")
          setSelectedIgo(res[0])
        }
      } else if (res[0]) {
        setSelectedIgo(res[0])
      }
    }

    const fetchLinks = async () => {
      try {
        const response = await fetchLinksThematics(
          sessionStorage.getItem("thematic"),
          selectedCountry.code,
          sessionStorage.getItem("search_igo")
        )

        if (response) {
          setLinks(response)
          const grouped = response.reduce((acc, link, index) => {
            const groupIndex = Math.floor(index / lengthGroupsLinks)
            if (!acc[groupIndex]) {
              acc[groupIndex] = []
            }
            acc[groupIndex].push(link)
            return acc
          }, [])
          setGroupsLinks(grouped)
        }

        const selectedLinkId = url.searchParams.get("link-id")
        if (selectedLinkId) {
          const selectedLink = response.find((link) => link.id === parseInt(selectedLinkId, 10))
          if (selectedLink) {
            setSelectedSite(selectedLink)
          } else {
            setSelectedSite(null)
            url.searchParams.delete("link-id")
            window.history.pushState({}, "", url)
            sessionStorage.removeItem("selected_site")
          }
        }
      } catch (error) {
        console.error("Error fetching thematic links:", error)
      }
    }

    const fetchSearchInCse = async () => {
      try {
        const response = await getSearchInCseThematic(sessionStorage.getItem("thematic"), selectedCountry.code)
        setSearchInCse(Boolean(response?.search_in_cse === 1))
      } catch (error) {
        console.error("Error fetching search in CSE:", error)
      }
    }

    fetchSearchInCse()
    fetchLinks()
    funIgo()
    setIgoReady(true)
  }, [lengthGroupsLinks, selectedCountry, url])

  useEffect(() => {
    scrollToBottom()
  }, [messages, loading])

  if (sessionStorage.getItem("search_country_country") !== null) {
    sessionStorage.removeItem("search_country_country")
  }
  if (sessionStorage.getItem("search_themes_country") !== null) {
    sessionStorage.removeItem("search_themes_country")
  }
  sessionStorage.setItem("listmiddle", "Zemus IA")

  const selectNewCountry = (nextCountry) => {
    setSelectedCountry(nextCountry)
    sessionStorage.setItem("search_country", JSON.stringify(nextCountry))
    window.location.reload()
  }

  const handleDisplayMap = () => {
    setDisplayMap((prev) => !prev)
  }

  return (
    <div className="zemus-ia-page">
      <ProfileHeader
        selectedCountry={selectedCountry}
        selectedTheme={selectedTheme}
        setSelectedCountry={(nextCountry) => selectNewCountry(nextCountry)}
        igo={igo}
        igoReady={igoReady}
        selectedIgo={selectedIgo}
        setSelectedIgo={(nextIgo) => setSelectedIgo(nextIgo)}
        links={links}
        setLinks={(nextLinks) => setLinks(nextLinks)}
        groupsLinks={groupsLinks}
        setGroupsLinks={(groups) => setGroupsLinks(groups)}
        selectedGroupLinks={selectedGroupLinks}
        setSelectedGroupLinks={(group) => setSelectedGroupLinks(group)}
        lengthGroupsLinks={lengthGroupsLinks}
        selectedSite={selectedSite}
        setSelectedSite={(site) => handleSiteSelect(site)}
        searchInCse={searchInCse}
        showSiteDropdown={showSiteDropdown}
        setShowSiteDropdown={(show) => setShowSiteDropdown(show)}
        handleSiteSelect={(site) => handleSiteSelect(site)}
        searchQuery={searchQuery}
        setSearchQuery={(query) => setSearchQuery(query)}
        handleSearch={handleSearch}
        advancedSearch={advancedSearch}
        setAdvancedSearch={setAdvancedSearch}
        advancedSearchDisabled={loading || (advancedSearch && (!canAdvancedSearch(searchQuery)))}
        advancedSearchHint={advancedSearch ? advancedMissing : ""}
        displayMap={() => handleDisplayMap()}
      />

      <div className="flex">
        <aside className="zemus-ia-sessions" aria-label="Conversations">
          <button className="zemus-ia-new-session" onClick={startNewConversation}>
            + Nouvelle conversation
          </button>
          {sessionsLoading && <p className="zemus-ia-session-status">Chargement...</p>}
          {!sessionsLoading && sessions.length === 0 && (
            <p className="zemus-ia-session-status">Aucune conversation enregistrée.</p>
          )}
          {sessions.map((session) => (
            <div key={session.id} className={`zemus-ia-session ${session.id === sessionId ? "active" : ""}`}>
              <button className="zemus-ia-session-select" onClick={() => selectSession(session.id)}>
                <strong>{session.name || "Conversation sans nom"}</strong>
                <small>{new Date(session.updated_at).toLocaleDateString("fr-FR")}</small>
              </button>
              <button className="zemus-ia-session-action" onClick={() => renameSession(session)} title="Renommer">
                Modifier
              </button>
              <button className="zemus-ia-session-action delete" onClick={() => removeSession(session)} title="Supprimer">
                Supprimer
              </button>
            </div>
          ))}
          {sessionError && <p className="zemus-ia-session-error">{sessionError}</p>}
        </aside>
        <div className="flex-1">
          {selectedIgo.name !== DEFAULT_IGO_LABEL && (
            <div className="flex flex-col-reverse xl:flex-row xl:justify-between items-center ml-2 mr-2">
              <h2 className="text-sm xl:text-xl">
                <button
                  className="underline"
                  onClick={() => {
                    sessionStorage.removeItem("search_themes_country")
                    sessionStorage.removeItem("search_country_country")
                    sessionStorage.setItem("search_country", JSON.stringify(countries.find((c) => c.code === "wd")))
                    sessionStorage.setItem("search_query", "")
                    sessionStorage.removeItem("thematic")
                    window.location.href = "/"
                  }}
                >
                  {selectedIgo.label}
                </button>
                <button
                  type="button"
                  className="ml-2 font-bold"
                  style={{ color: "#000" }}
                  aria-label="Retirer le forum global de la recherche"
                  title="Retirer le forum global de la recherche"
                  onClick={() => {
                    sessionStorage.setItem("search_query", "")
                    sessionStorage.removeItem("search_igo")
                    sessionStorage.removeItem("selected_igo")
                    sessionStorage.removeItem("thematic")
                    window.location.href = "/zemus-ia"
                  }}
                >
                  ✕
                </button>
              </h2>
              {selectedIgo.image && (
                <a href={selectedIgo.link} target="_blank" rel="noopener noreferrer">
                  <img className="w-auto h-16 xl:h-20" src={selectedIgo.image} alt={`Logo ${selectedIgo.name}`} />
                </a>
              )}
            </div>
          )}

          <Map
            selectedCountry={selectedCountry}
            setSelectedCountry={(nextCountry) => selectNewCountry(nextCountry)}
            displayMap={displayMap}
            setDisplayMap={(hide) => setDisplayMap(hide)}
          />

          <div className="zemus-ia-chat-shell" ref={chatShellRef}>
            <div key={`chat-${sessionId ?? "new"}`} className="zemus-ia-messages notranslate" translate="no">
              {historyLoading && <div className="zemus-ia-session-status">Chargement de la conversation...</div>}
              {messages.length === 0 && !loading && (
                <div className="zemus-ia-empty-state">
                  <h1 className="zemus-ia-title">Zemus IA</h1>
                  <p className="zemus-ia-subtitle">
                    Utilisez la barre de recherche ci-dessus pour lancer une conversation IA sur votre pays,
                    thematique ou source selectionnee.
                  </p>
                  <p className="zemus-ia-example">
                    Exemple: Quels sont les dernieres actualites sur le roi Charles III en Angleterre ?
                  </p>
                </div>
              )}

              {messages.map((msg, index) => (
                <div key={msg.id ?? `${msg.role}-${index}`} className={`zemus-ia-row ${msg.role}`}>
                  <div className="zemus-ia-bubble">
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                            {props.children}
                          </a>
                        )
                      }}
                    >
                      {normalizeLinksForMarkdown(msg.text)}
                    </ReactMarkdown>
                    {msg.role === "assistant" && msg.suggestions?.length > 0 && (
                      <div className="zemus-ia-suggestions" aria-label="Suggestions de recherche avancée">
                        {msg.suggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            className="zemus-ia-suggestion"
                            disabled={loading}
                            onClick={() => sendMessage(suggestion, Boolean(msg.advanced))}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                    {msg.role === "assistant" && msg.scope && (
                      <div className="zemus-ia-scope text-sm mt-2">
                        {msg.scope.thematic && <p><strong>Thématique :</strong> {msg.scope.thematic}</p>}
                        {msg.scope.selectedThematics.length > 0 && (
                          <div>
                            <strong>Périmètre identifié automatiquement :</strong>
                            <ul>
                              {msg.scope.selectedThematics.map((item) => (
                                <li key={item.id}>
                                  {item.path} {item.role ? `(${item.role})` : ""}
                                  {item.justification ? ` — ${item.justification}` : ""}
                                  {Array.isArray(item.countries) && item.countries.length > 0
                                    ? ` [${item.countries.map((code) => String(code).toUpperCase()).join(", ")}]`
                                    : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {msg.scope.countries.length > 0 && (
                          <p><strong>Pays :</strong> {msg.scope.countries.map((code) => String(code).toUpperCase()).join(", ")}</p>
                        )}
                        {msg.scope.sources.length > 0 && (
                          <details>
                            <summary>Sources autorisées ({msg.scope.sources.length})</summary>
                            <ul>
                              {msg.scope.sources.map((source) => (
                                <li key={`${source.country}-${source.link}`}>
                                  <a href={source.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">
                                    {source.label || source.link}
                                  </a>
                                  {source.countryName ? ` (${source.countryName})` : ""}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                    {msg.retry && (
                      <button
                        type="button"
                        className="zemus-ia-suggestion"
                        disabled={loading}
                        onClick={() => sendMessage(msg.retry.query, msg.retry.advanced)}
                      >
                        Réessayer
                      </button>
                    )}
                    {msg.role === "assistant" && (() => {
                      const messageId = msg.id ?? `assistant-${index}`
                      const shareLinks = getShareLinks(msg.text || "")
                      return (
                        <div className="zemus-ia-message-actions">
                          {copyStatus[messageId] && (
                            <span className="zemus-ia-copy-status" role="status">
                              {copyStatus[messageId]}
                            </span>
                          )}
                          <button
                            type="button"
                            className="zemus-ia-icon-action"
                            aria-label="Copier la réponse"
                            title="Copier la réponse"
                            onClick={() => copyAssistantMessage(messageId, msg.text || "")}
                          >
                            <ContentCopyOutlined fontSize="small" />
                          </button>
                          <div className="zemus-ia-share-control">
                            <button
                              type="button"
                              className="zemus-ia-icon-action"
                              aria-label="Partager la réponse"
                              title="Partager la réponse"
                              aria-expanded={shareMenuId === messageId}
                              onClick={() => setShareMenuId((currentId) => currentId === messageId ? null : messageId)}
                            >
                              <ShareOutlined fontSize="small" />
                            </button>
                            {shareMenuId === messageId && (
                              <div className="zemus-ia-share-menu" aria-label="Partager sur">
                                {shareLinks.map((shareLink) => (
                                  <a
                                    key={shareLink.label}
                                    href={shareLink.href}
                                    target={shareLink.label === "E-mail" ? undefined : "_blank"}
                                    rel={shareLink.label === "E-mail" ? undefined : "noopener noreferrer"}
                                    onClick={() => {
                                      if (shareLink.copyText) {
                                        copyAssistantMessage(messageId, msg.text || "", shareLink.copyText)
                                      }
                                      setShareMenuId(null)
                                    }}
                                  >
                                    {shareLink.label}
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="zemus-ia-row assistant">
                  <div className="zemus-ia-bubble">
                    {advancedSearch || messages[messages.length - 1]?.advanced
                      ? "Analyse en cours, cela peut prendre plusieurs dizaines de secondes..."
                      : "Generation en cours..."}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        <div id="pays_large" className="w-60 bg-neutral-50">
          <CountryBar
            selectedCountry={selectedCountry}
            setSelectedCountry={(nextCountry) => selectNewCountry(nextCountry)}
            selectedIgo={selectedIgo}
            setSelectedIgo={(nextIgo) => setSelectedIgo(nextIgo)}
            displayMap={() => handleDisplayMap()}
            isMobile={false}
            igo={igo}
            igoReady={igoReady}
          />
        </div>
      </div>

      <button 
        onClick={scrollToTop} 
        className="fixed bottom-6 right-6 bg-[#009AAD] text-white p-5 rounded-full shadow-lg hover:bg-[#007B8F] transition-colors"
        title="Remonter au haut du chat"
      >
        ↑
      </button>
    </div>
  )
}
